// The form definition the builder edits and the server stores (forms.definition, and a frozen copy
// per published version). It is Meta-agnostic: generator.ts turns it into Flow JSON.
//
// Identity rules (what WaDesk got wrong):
// - every screen, component and option has a stable id generated once and never derived from its
//   position or title; reordering or renaming never changes an id;
// - a field's id is its Flow JSON `name` and its key in the `complete` payload, so a submission maps
//   back to the field (and its option ids back to titles) through the definition of the version the
//   customer filled in.
//
// The zod schema here is structural (types, id formats, generous size caps against abuse), so a
// half-edited draft can be saved; validate.ts checks Meta's limits before publishing.

import { z } from 'zod';
import { RESERVED_PAYLOAD_KEYS } from './limits.ts';

export const SCHEMA_VERSION = 1;

/** Letters only (the Meta screen id is its upper-case form, which must be letters/underscores). */
export const SCREEN_ID = /^[a-z]{4,32}$/;
/** Also the Flow JSON field `name` and the payload key. */
export const COMPONENT_ID = /^[a-z][a-z0-9_]{2,39}$/;
export const OPTION_ID = /^[a-z][a-z0-9_]{1,39}$/;
/** Chatwoot custom attribute keys. */
export const ATTRIBUTE_KEY = /^[a-z][a-z0-9_]{0,59}$/;
/** Chatwoot label titles (stored lower-case). */
export const LABEL_TITLE = /^[\p{L}\p{N}_-]{1,255}$/u;
export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const text = (max: number) => z.string().max(max);
const optionalText = (max: number) => z.string().max(max).nullish();
const count = z.number().int().min(0).max(100_000).nullish();

const screenId = z
  .string()
  .regex(SCREEN_ID, 'screen ids are 4–32 lower-case letters')
  .refine((id) => id !== 'success', 'success is reserved');
const componentId = z
  .string()
  .regex(COMPONENT_ID, 'component ids are lower-case letters, digits and _ (3–40, starting with a letter)')
  .refine((id) => !(RESERVED_PAYLOAD_KEYS as readonly string[]).includes(id), 'this id is reserved');

export const optionSchema = z.object({
  id: z.string().regex(OPTION_ID, 'option ids are lower-case letters, digits and _'),
  title: text(500),
  description: optionalText(1000),
  /** A contact label added when this option is chosen. */
  addLabel: optionalText(300),
});

const field = {
  id: componentId,
  label: text(500),
  required: z.boolean(),
  /** Custom contact attribute key the answer is saved to. */
  saveTo: z.string().regex(ATTRIBUTE_KEY, 'attribute keys are lower-case letters, digits and _').nullish(),
};

export const componentSchema = z.discriminatedUnion('type', [
  z.object({ id: componentId, type: z.literal('heading'), text: text(500) }),
  z.object({ id: componentId, type: z.literal('subheading'), text: text(500) }),
  z.object({ id: componentId, type: z.literal('body'), text: text(10_000) }),
  z.object({
    id: componentId,
    type: z.literal('image'),
    /** forms_images.id of this account. */
    imageId: z
      .string()
      .regex(/^[1-9]\d{0,18}$/)
      .nullish(),
    altText: optionalText(500),
    height: z.number().int().min(1).max(10_000).nullish(),
  }),
  z.object({
    ...field,
    type: z.literal('short_text'),
    inputType: z.enum(['text', 'number', 'email', 'phone']),
    helperText: optionalText(500),
    minChars: count,
    maxChars: count,
  }),
  z.object({
    ...field,
    type: z.literal('long_text'),
    helperText: optionalText(500),
    maxLength: count,
  }),
  z.object({ ...field, type: z.literal('dropdown'), options: z.array(optionSchema).max(250) }),
  z.object({
    ...field,
    type: z.literal('radio'),
    description: optionalText(1000),
    options: z.array(optionSchema).max(250),
  }),
  z.object({
    ...field,
    type: z.literal('checkbox'),
    description: optionalText(1000),
    minSelected: count,
    maxSelected: count,
    options: z.array(optionSchema).max(250),
  }),
  z.object({
    ...field,
    type: z.literal('date'),
    helperText: optionalText(500),
    minDate: z.string().regex(ISO_DATE).nullish(),
    maxDate: z.string().regex(ISO_DATE).nullish(),
  }),
  z.object({
    ...field,
    type: z.literal('opt_in'),
    /** https link behind the opt-in's "Read more". */
    readMoreUrl: optionalText(2000),
  }),
]);

export const screenSchema = z.object({
  id: screenId,
  title: text(500),
  /** The Footer button: "Continue" on inner screens, "Submit" on the last. */
  buttonLabel: text(500),
  components: z.array(componentSchema).max(100),
});

export const definitionSchema = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    screens: z.array(screenSchema).min(1).max(50),
  })
  .superRefine((definition, context) => {
    const seen = new Set<string>();
    definition.screens.forEach((screen, screenIndex) => {
      if (seen.has(screen.id)) {
        context.addIssue({
          code: 'custom',
          message: `duplicate id ${screen.id}`,
          path: ['screens', screenIndex, 'id'],
        });
      }
      seen.add(screen.id);
      screen.components.forEach((component, componentIndex) => {
        if (seen.has(component.id)) {
          context.addIssue({
            code: 'custom',
            message: `duplicate id ${component.id}`,
            path: ['screens', screenIndex, 'components', componentIndex, 'id'],
          });
        }
        seen.add(component.id);
        if ('options' in component) {
          const optionIds = new Set<string>();
          component.options.forEach((option, optionIndex) => {
            if (optionIds.has(option.id)) {
              context.addIssue({
                code: 'custom',
                message: `duplicate option id ${option.id}`,
                path: ['screens', screenIndex, 'components', componentIndex, 'options', optionIndex, 'id'],
              });
            }
            optionIds.add(option.id);
          });
        }
      });
    });
  });

export type FormDefinition = z.infer<typeof definitionSchema>;
export type Screen = z.infer<typeof screenSchema>;
export type Component = z.infer<typeof componentSchema>;
export type ComponentType = Component['type'];
export type ChoiceOption = z.infer<typeof optionSchema>;

export type FieldComponent = Extract<
  Component,
  { type: 'short_text' | 'long_text' | 'dropdown' | 'radio' | 'checkbox' | 'date' | 'opt_in' }
>;
export type ChoiceComponent = Extract<Component, { type: 'dropdown' | 'radio' | 'checkbox' }>;
export type FieldType = FieldComponent['type'];

export const FIELD_TYPES: readonly FieldType[] = [
  'short_text',
  'long_text',
  'dropdown',
  'radio',
  'checkbox',
  'date',
  'opt_in',
];

export const isField = (component: Component): component is FieldComponent =>
  (FIELD_TYPES as readonly string[]).includes(component.type);

export const isChoice = (component: Component): component is ChoiceComponent =>
  component.type === 'dropdown' || component.type === 'radio' || component.type === 'checkbox';

/** Every input field with its screen, in form order. */
export function fieldsOf(definition: FormDefinition): { screen: Screen; field: FieldComponent }[] {
  return definition.screens.flatMap((screen) =>
    screen.components.filter(isField).map((field) => ({ screen, field })),
  );
}

/** The Meta screen id: the stable letters-only id, upper-cased. */
export const metaScreenId = (screen: Pick<Screen, 'id'>): string => screen.id.toUpperCase();

/** Trimmed text, or undefined when empty (Meta rejects empty strings from Flow JSON 6.0). */
export function present(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export const COMPONENT_LABELS: Record<ComponentType, string> = {
  heading: 'Heading',
  subheading: 'Subheading',
  body: 'Body text',
  image: 'Image',
  short_text: 'Short answer',
  long_text: 'Paragraph',
  dropdown: 'Dropdown',
  radio: 'Single choice',
  checkbox: 'Checkboxes',
  date: 'Date',
  opt_in: 'Opt-in',
};
